import { Injectable } from '@angular/core';
import {
  DataViewDataModel,
  GenericViewDisplayConfig,
  ViewSourceParameter,
} from '@osos/shared/api';
import {
  DataViewFilterFirstConfiguration,
  DataViewsPresenterModel,
  DataViewStyle,
  DefaultSidebarEnum,
  EntryActionOperationEnum,
  PageContainerLayoutMode,
  ShowWorkflowConfig,
  ViewDisplayOptions,
  ViewSourceParameterValueTypeEnum,
} from '@osos/shared/types';
import { ParserBase } from '../../bases/parser/parser-base';
type PageContainerLayoutType = { mode: PageContainerLayoutMode };

@Injectable()
export class DataViewsParserV1Service extends ParserBase<
  DataViewDataModel,
  DataViewsPresenterModel
> {
  constructor() {
    super(DataViewDataModel, DataViewsPresenterModel, 1);
  }

  getPresenterModel(model: DataViewDataModel): DataViewsPresenterModel {
    // Calculate sourceOptions flags
    const sourceOptionsWithFlags = this.calculateSourceOptionsFlags(
      model.sourceOptions,
    );
    // Generate filterFirst configuration from source parameters if needed
    const filterFirstConfig = this.generateFilterFirstFromSourceParameters(
      model.sourceOptions?.sourceParameters || [],
    );

    return {
      specificationVersion: this.version,
      dataEntity: model.dataEntity,
      dataSource: model.dataSource,
      sourceOptions: sourceOptionsWithFlags,

      description: model.description,
      domain: model.domain,
      implicitType: model.implicitType,
      editConfiguration: {
        enabled: model.editConfiguration?.enabled,
        adminActions: model.editConfiguration?.adminActions,
        allowCreateEntryAsDraft:
          model.editConfiguration?.allowCreateEntryAsDraft,
        allowQuickCreate: model.editConfiguration?.allowQuickCreate,
        allowDuplicateEntry: model.editConfiguration?.allowDuplicateEntry,
        showWorkflowAction: model.editConfiguration?.showWorkflowAction,
        showSidebar: model.editConfiguration?.showSidebar,
        supportedOperations: model.editConfiguration?.supportedOperations?.map(
          (opt) => ({
            afterSave: opt.afterSave,
            operationType: opt.operation,
            fields: opt.fields,
            allowSaveInvalidEntries: opt.allowSaveInvalidEntries,

            requiredFields: opt.requiredFields,
            ...(model.editConfiguration?.allowInlineEdit &&
              opt.operation === EntryActionOperationEnum.AllowEditEntries && {
                allowInlineEdit: true,
              }),
            ...(model.editConfiguration?.allowCreateEntryAsDraft &&
              opt.operation ===
                EntryActionOperationEnum.AllowCreateNewEntries && {
                allowCreateEntryAsDraft: true,
              }),
            ...(model.editConfiguration?.allowQuickCreate &&
              opt.operation ===
                EntryActionOperationEnum.AllowCreateNewEntries && {
                allowQuickCreate: true,
              }),
            ...(model.editConfiguration?.allowDuplicateEntry &&
              opt.operation ===
                EntryActionOperationEnum.AllowCreateNewEntries && {
                allowDuplicateEntry: true,
              }),
          }),
        ),
      },
      fields: model.fields,
      icon: model.icon,
      // Rahaf todo remove this
      id: model.id ?? model['_id'],
      isPinned: model.isPinned,
      isPublished: model.isPublished,
      isImplicit: model.isImplicit,
      name: model.name,
      viewConfiguration: {
        ...model.viewConfiguration,
        filterFirst: filterFirstConfig ?? model.viewConfiguration?.filterFirst,
        defaultSorting: model.viewConfiguration?.defaultSorting?.map(
          (sort) => ({
            sortBy: sort.field,
            sortDir: sort.ascending ? 'asc' : 'desc',
          }),
        ),
      },
      entriesConfiguration: {
        ...model.entriesConfiguration,
        history: {
          ...model.entriesConfiguration?.history,
          showTriggerHistory: model.entriesConfiguration?.showTriggerHistory,
        },
        allowedTemplates: model.entriesConfiguration?.allowedTemplates,
        showWorkflowHistory: this.normalizeWorkflowHistory(
          model.entriesConfiguration?.showWorkflowHistory,
        ),
        supportPaginationBetweenEntries: true,
      },
      anonymous: model.anonymous,
      authenticated: model.authenticated,
      permissions: model.permissions,
      displayConfig: model?.displayConfig
        ? this._mapDisplayConfigToPresenterModel(model.displayConfig)
        : this._getDefaultDisplayOptions(),
      dashboardConfiguration: model.dashboardConfiguration,
      sideBarConfig: model?.sideBarConfig ?? {
        openMode: 0,
      },
      relations: model?.relations,
      module: model.module,
      displayOrder: model.displayOrder,
      systemButtons: model.systemButtons ?? [],
      systemButtonsOrder: model.systemButtonsOrder ?? [],
      screenOrder: model.screenOrder ?? [],
      uiAttributes: model.uiAttributes as unknown as DataViewStyle,
      qrCode: model.qrCode,
      sharing: {
        ...model.sharing,
        level: model.sharing?.level?.id,
      },
      updateIdentifier: model.updateIdentifier,
      redirectionRules: model.redirectionRules
        ? JSON.stringify(model.redirectionRules)
        : undefined,
      help: model.help,
      defaultViewTemplate: model.defaultViewTemplate,
    } as DataViewsPresenterModel;
  }

  getDataModel(presenter: DataViewsPresenterModel): DataViewDataModel {
    // Calculate sourceOptions flags
    const sourceOptionsWithFlags = this.calculateSourceOptionsFlags(
      presenter.sourceOptions,
    );

    // Generate filterFirst configuration from source parameters if needed
    const filterFirstConfig = this.generateFilterFirstFromSourceParameters(
      presenter.sourceOptions?.sourceParameters || [],
    );

    const model = {
      name: presenter.name,
      dataEntity: presenter.dataEntity,
      dataSource: presenter.dataSource,
      sourceOptions: sourceOptionsWithFlags,
      description: presenter.description,
      fields: presenter.fields,
      icon: presenter.icon,
      isPinned: presenter.isPinned,
      isPublished: presenter.isPublished,
      domain: presenter.domain ?? undefined,
      specificationVersion: this.version,
      implicitType: presenter.implicitType,
      editConfiguration: {
        enabled: presenter.editConfiguration?.enabled,
        adminActions: presenter?.editConfiguration.adminActions,
        allowInlineEdit:
          presenter?.editConfiguration?.supportedOperations?.find(
            (operation) =>
              operation.operationType ===
              EntryActionOperationEnum.AllowEditEntries,
          )?.allowInlineEdit || false,
        allowCreateEntryAsDraft:
          presenter?.editConfiguration?.supportedOperations?.find(
            (operation) =>
              operation.operationType ===
              EntryActionOperationEnum.AllowCreateNewEntries,
          )?.allowCreateEntryAsDraft || false,
        allowQuickCreate:
          presenter?.editConfiguration?.supportedOperations?.find(
            (operation) =>
              operation.operationType ===
              EntryActionOperationEnum.AllowCreateNewEntries,
          )?.allowQuickCreate || false,
        allowDuplicateEntry:
          presenter?.editConfiguration?.supportedOperations?.find(
            (operation) =>
              operation.operationType ===
              EntryActionOperationEnum.AllowCreateNewEntries,
          )?.allowDuplicateEntry || undefined,
        showWorkflowAction: presenter?.editConfiguration?.showWorkflowAction,
        showSidebar: presenter?.editConfiguration?.showSidebar,
        supportedOperations:
          presenter.editConfiguration?.supportedOperations?.map((opt) => ({
            afterSave: opt.afterSave,
            operation: opt.operationType,
            fields: opt.fields,
            requiredFields: opt.requiredFields,
            allowSaveInvalidEntries: opt.allowSaveInvalidEntries,
          })),
      },
      viewConfiguration: {
        ...presenter.viewConfiguration,
        filterFirst:
          filterFirstConfig ?? presenter.viewConfiguration.filterFirst,
        defaultSorting: presenter.viewConfiguration.defaultSorting?.map(
          (sort) => ({
            field: sort.sortBy,
            ascending: sort.sortDir === 'asc',
          }),
        ),
      },
      entriesConfiguration: {
        enabled: presenter.entriesConfiguration.enabled,
        fields: presenter.entriesConfiguration.fields,
        discussions: presenter.entriesConfiguration.discussions,
        history: {
          enabled: presenter.entriesConfiguration.history?.enabled ?? false,
          showChanger:
            presenter.entriesConfiguration.history?.showChanger ?? false,
          showChanges:
            presenter.entriesConfiguration.history?.showChanges ?? false,
          showReactionHistory:
            presenter.entriesConfiguration.history?.showTriggerHistory
              ?.enabled ?? false,
        },
        showTriggerHistory: {
          enabled:
            presenter.entriesConfiguration.history?.showTriggerHistory
              ?.enabled ?? false,
          showTriggerDetails:
            presenter.entriesConfiguration.history?.showTriggerHistory
              ?.showTriggerDetails ?? false,
        },
        customTemplate: presenter.entriesConfiguration.customTemplate,
        supportPaginationBetweenEntries: true,
        // presenter.entriesConfiguration.supportPaginationBetweenEntries,
        allowedTemplates:
          presenter.entriesConfiguration?.allowedTemplates ?? [],
        showWorkflowHistory: this.normalizeWorkflowHistory(
          presenter.entriesConfiguration.showWorkflowHistory,
        ),
        showEmailConfig: presenter.entriesConfiguration?.showEmailConfig ?? [],
        defaultSidebar:
          presenter.entriesConfiguration?.defaultSidebar ??
          DefaultSidebarEnum.None,
        showInformation: presenter.entriesConfiguration?.showInformation,
      },
      permissions: presenter.permissions,
      displayConfig: this._mapDisplayConfigToDataModel(presenter.displayConfig),
      anonymous: {
        defaultView: presenter.anonymous.defaultView,
        enabled: presenter.anonymous.enabled,
        urls: presenter.anonymous.urls ?? [],
      },
      authenticated: {
        defaultView: presenter.authenticated.defaultView,
        enabled: presenter.authenticated.enabled,
        isRestricted: presenter.authenticated.isRestricted,
      },
      dashboardConfiguration: presenter.dashboardConfiguration,
      sideBarConfig: presenter.sideBarConfig,
      relations: presenter.relations,
      displayOrder:
        presenter.displayOrder !== null ? presenter.displayOrder : undefined,
      systemButtons: presenter.systemButtons ?? [],
      systemButtonsOrder: presenter.systemButtonsOrder ?? [],
      screenOrder: presenter.screenOrder ?? [],
      uiAttributes: presenter.uiAttributes,
      qrCode: presenter.qrCode,
      sharing: {
        ...presenter.sharing,
        level: {
          id: presenter.sharing.level,
        },
      },
      updateIdentifier: presenter.updateIdentifier,
      redirectionRules: presenter?.redirectionRules
        ? JSON.parse(presenter.redirectionRules)
        : undefined,
      help: presenter.help ?? null,
      defaultViewTemplate: presenter.defaultViewTemplate,
    } as DataViewDataModel;

    return model;
  }

  /**
   * Calculate sourceOptions flags based on source parameters
   */
  private calculateSourceOptionsFlags(sourceOptions: any): any {
    if (!sourceOptions) {
      return sourceOptions;
    }

    const parameters = sourceOptions.sourceParameters || [];
    const hasParameters = parameters.length > 0;
    const hasFilterFirstParameters =
      hasParameters &&
      parameters.some(
        (param: any) =>
          param?.valueType === ViewSourceParameterValueTypeEnum.FilterFirst,
      );

    return {
      ...sourceOptions,
      isParameterized: hasParameters,
      isFilterFirstEnabled: hasFilterFirstParameters,
    };
  }

  private generateFilterFirstFromSourceParameters(
    sourceParameters: ViewSourceParameter[],
  ): DataViewFilterFirstConfiguration | null {
    const filterFirstParams = sourceParameters.filter(
      (param) =>
        param.valueType === ViewSourceParameterValueTypeEnum.FilterFirst,
    );

    // If there are FilterFirst parameters, generate configuration from them
    if (filterFirstParams?.length > 0) {
      const fields = filterFirstParams.map((param) => ({
        fieldName: param.name,
        displayName: param.value,
      }));

      return {
        enabled: true,
        fields,
      };
    }

    return null;
  }

  private normalizeWorkflowHistory(
    config?: ShowWorkflowConfig | null,
  ): ShowWorkflowConfig | undefined {
    if (!config) {
      return config ?? undefined;
    }

    return {
      ...config,
      enableHistoryPath: config.enableHistoryPath ?? true,
    };
  }
  private _mapDisplayConfigToPresenterModel(
    config: GenericViewDisplayConfig,
  ): ViewDisplayOptions {
    const { pageContainerLayout, ...model } = config;

    return {
      ...model,
      pageContainedWidth:
        pageContainerLayout?.mode === PageContainerLayoutMode.Contained,
    };
  }
  private _mapDisplayConfigToDataModel(
    config: ViewDisplayOptions,
  ): GenericViewDisplayConfig {
    const { pageContainedWidth, ...model } = config;

    return {
      ...model,
      pageContainerLayout: {
        mode: pageContainedWidth
          ? PageContainerLayoutMode.Contained
          : PageContainerLayoutMode.Fluid,
      } satisfies PageContainerLayoutType,
    };
  }

  private _getDefaultDisplayOptions(): ViewDisplayOptions {
    return {
      transparent: false,
      fullPage: false,
      outerSpacing: true,
      showPageHeader: true,
      pageContainedWidth: false,
    };
  }
}
